import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, onSnapshot, collection, query, where, orderBy, getDocs, updateDoc } from 'firebase/firestore'; 
import { auth, db } from '../firebase'; 
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { sendEmailVerification, updateProfile } from 'firebase/auth';

const PartnerDashboard = () => {
    const { t, i18n } = useTranslation();
    const [user, loadingAuth] = useAuthState(auth);
    const [partnerData, setPartnerData] = useState(null); 
    const [referralsList, setReferralsList] = useState([]); 
    const [teamList, setTeamList] = useState([]); 
    const [activeTab, setActiveTab] = useState('Overview');
    const [isLoading, setIsLoading] = useState(true);

    const partnerId = user?.uid;
    const origin = typeof window !== 'undefined' && window.location.origin ? window.location.origin : 'https://aulice.ca';
    const referralLink = partnerId ? `${origin}/signup?ref=${partnerId}` : 'Loading...';

    // ✅ ASSET PATH (Kept exactly as working)
    const currentLang = i18n.language?.split('-')[0] || 'en'; 
    const assetsPath = `/marketing/${currentLang}`;

    // ✅ FEATURE: Add Name Logic
    const handleUpdateName = async () => {
        const firstName = prompt("Please enter your First Name:");
        const lastName = prompt("Please enter your Last Name:");
        if (firstName && lastName) {
            const fullName = `${firstName} ${lastName}`;
            try {
                await updateProfile(user, { displayName: fullName });
                // Sync with Firestore
                await updateDoc(doc(db, 'users', user.uid), {
                    firstName,
                    lastName,
                    displayName: fullName
                });
                window.location.reload(); 
            } catch (err) {
                console.error(err);
                alert("Could not update name. Try again.");
            }
        }
    };

    const handleResendVerification = async () => {
        if (user) {
            try {
                await sendEmailVerification(user);
                alert(`Verification email resent to ${user.email}.`);
            } catch (err) {
                alert("Error: " + err.message);
            }
        }
    };

    const copyLink = () => { navigator.clipboard.writeText(referralLink); alert('Link copied!'); };
    
    const copyEmailScript = (type) => {
        const subject = t(`partner_dashboard.email_scripts.${type}_subject`) || "Join me on Aulice";
        const bodyRaw = t(`partner_dashboard.email_scripts.${type}_body`) || "Check out this link: {{link}}";
        const body = bodyRaw.replace('{{link}}', referralLink);
        navigator.clipboard.writeText(`SUBJECT: ${subject}\n\n${body}`);
        alert("Email script copied!");
    };

    useEffect(() => {
        if (loadingAuth) return;
        if (!user) { setIsLoading(false); return; }

        const partnerRef = doc(db, 'partners', user.uid);
        const unsubscribe = onSnapshot(partnerRef, (docSnap) => {
            if (docSnap.exists()) setPartnerData(docSnap.data());
            else setPartnerData({ totalEarnings: 0, referralCount: 0 });
            setIsLoading(false); 
        });

        const fetchReferrals = async () => {
            try {
                const refsQuery = query(collection(db, 'partners', user.uid, 'referrals'), orderBy('date', 'desc'));
                const snapshot = await getDocs(refsQuery);
                setReferralsList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (err) { console.log("Referral fetch error", err); }
        };

        const fetchTeam = async () => {
            try {
                const teamQuery = query(collection(db, 'users'), where('referredBy', '==', user.uid));
                const snapshot = await getDocs(teamQuery);
                setTeamList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (err) { console.log("Team fetch error", err); }
        };

        fetchReferrals();
        fetchTeam();
        return () => unsubscribe(); 
    }, [user, loadingAuth]);

    const referralCount = partnerData?.referralCount || 0;
    const earnings = partnerData?.totalEarnings || 0;
    const nextMilestone = 5; 
    const isSenior = referralCount >= nextMilestone;
    const progressPercent = Math.min((referralCount / nextMilestone) * 100, 100);
    const isStripeConnected = partnerData?.stripeId ? true : false;

    if (loadingAuth || isLoading) return <div className="p-10 text-center">Loading...</div>;
    if (!user) return <div className="p-10 text-center">Please Log In.</div>;

    return (
        <div className="max-w-6xl mx-auto py-6 px-4 md:py-10">
            {/* HEADER: ✅ Phone Friendly + Name Feature */}
            <header className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-4 gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-extrabold text-indigo-700">Partner Dashboard</h1>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <p className="text-lg md:text-xl font-semibold text-gray-800">
                            {t('partner_dashboard.welcome', { name: user.displayName?.split(' ')[0] || 'Partner' }) || `Welcome, ${user.displayName || 'Partner'}`}
                        </p>
                        {!user.displayName && (
                            <button onClick={handleUpdateName} className="text-xs text-indigo-600 underline font-bold hover:text-indigo-800">
                                + Add Name
                            </button>
                        )}
                    </div>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    
                    {!user.emailVerified && (
                        <div className="mt-2 text-xs text-yellow-800 bg-yellow-50 p-2 rounded inline-block">
                            <span className="mr-1">⚠️ Email Unverified</span>
                            <button onClick={handleResendVerification} className="underline font-bold hover:text-yellow-900">Resend</button>
                        </div>
                    )}
                </div>

                <div className={`px-4 py-1 rounded-full text-white text-sm md:text-base font-bold shadow ${isSenior ? 'bg-yellow-500' : 'bg-blue-500'}`}>
                    {isSenior ? (t('partner_dashboard.senior_badge') || 'Senior Partner') : (t('partner_dashboard.junior_badge') || 'Junior Partner')}
                </div>
            </header>

            {/* TABS: ✅ Phone Friendly + Correct Translation Logic */}
            <div className="border-b mb-6 overflow-x-auto whitespace-nowrap -mx-4 px-4 md:mx-0 md:px-0">
                <div className="flex space-x-6 pb-2">
                    {['Overview', 'Instructions', 'Marketing Assets', 'Team Performance', 'Settings'].map(tabKey => (
                        <button 
                            key={tabKey}
                            onClick={() => setActiveTab(tabKey)}
                            className={`pb-2 text-sm md:text-base ${activeTab === tabKey ? 'border-b-2 border-indigo-600 text-indigo-600 font-bold' : 'text-gray-500'}`}
                        >
                            {/* ✅ RESTORED ORIGINAL MAPPING LOGIC FOR LABELS */}
                            {t(`partner_dashboard.tabs.${tabKey === 'Marketing Assets' ? 'assets' : tabKey === 'Team Performance' ? 'team' : tabKey.toLowerCase()}`, tabKey)}
                        </button>
                    ))}
                </div>
            </div>

            {/* TAB: OVERVIEW */}
            {activeTab === 'Overview' && (
                <>
                    <div className="mb-8 bg-white p-6 rounded-xl shadow">
                        <div className="flex justify-between mb-2 text-sm md:text-base">
                            <span className="font-bold text-gray-700">Progress to Senior Partner</span>
                            <span className="text-indigo-600 font-bold">{referralCount} / {nextMilestone} Referrals</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                            <div className="bg-indigo-600 h-4 rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }}></div>
                        </div>
                    </div>
                    {/* ✅ Phone Friendly Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                        <div className="bg-white p-6 rounded-xl shadow border-l-4 border-green-500">
                            <p className="text-sm text-gray-500 font-medium">Total Earnings</p>
                            <h2 className="text-3xl md:text-4xl font-bold text-green-600">${earnings.toFixed(2)}</h2>
                            {!isStripeConnected && <Link to="/partner-signup" className="text-xs text-red-600 font-bold hover:underline block mt-2">⚠️ Connect Stripe</Link>}
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow border-l-4 border-indigo-500">
                            <p className="text-sm text-gray-500 font-medium">Direct Sales</p>
                            <h2 className="text-3xl md:text-4xl font-bold text-indigo-600">{referralCount}</h2>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow">
                            <p className="text-sm text-gray-500 font-medium">Your Link</p>
                            <div className="flex mt-2">
                                <input readOnly value={referralLink} className="flex-1 p-2 border rounded-l bg-gray-50 text-xs"/>
                                <button onClick={copyLink} className="bg-indigo-600 text-white px-3 rounded-r text-sm">Copy</button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* TAB: INSTRUCTIONS */}
            {activeTab === 'Instructions' && (
                <div className="bg-white p-4 md:p-8 rounded shadow max-w-4xl mx-auto">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6 border-b pb-2">{t('partner_dashboard.instructions.main_title')}</h2>
                    <div className="space-y-8">
                        <div className="flex gap-4">
                            <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold text-lg md:text-xl">1</div>
                            <div>
                                <h3 className="text-base md:text-lg font-bold text-gray-800">{t('partner_dashboard.instructions.step1.title')}</h3>
                                <p className="text-sm md:text-base text-gray-600 mt-1"><Trans i18nKey="partner_dashboard.instructions.step1.text" components={{ bold: <span className="font-bold text-indigo-600" /> }} /></p>
                                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                                    <a href="https://www.linkedin.com/company/aulice-pro-mastery" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">LinkedIn</a>
                                    <a href="https://x.com/AulicePro" target="_blank" rel="noreferrer" className="text-black hover:underline">X (Twitter)</a>
                                    <a href="https://www.instagram.com/aulice_pro/" target="_blank" rel="noreferrer" className="text-pink-600 hover:underline">Instagram</a>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold text-lg md:text-xl">2</div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base md:text-lg font-bold text-gray-800">{t('partner_dashboard.instructions.step2.title')}</h3>
                                <p className="text-sm md:text-base text-gray-600 mt-1"><Trans i18nKey="partner_dashboard.instructions.step2.text" components={{ green: <span className="font-bold text-green-600" /> }} /></p>
                                <div className="mt-2 bg-gray-50 p-2 rounded border flex items-center overflow-hidden">
                                    <code className="text-xs flex-1 text-gray-700 truncate">{referralLink}</code>
                                    <button onClick={copyLink} className="text-xs bg-indigo-600 text-white px-2 py-1 rounded ml-2 flex-shrink-0">Copy</button>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold text-lg md:text-xl">3</div>
                            <div>
                                <h3 className="text-base md:text-lg font-bold text-gray-800">{t('partner_dashboard.instructions.step3.title')}</h3>
                                <p className="text-sm md:text-base text-gray-600 mt-1"><Trans i18nKey="partner_dashboard.instructions.step3.text" components={{ indigo: <span className="font-bold text-indigo-600" />, gold: <span className="font-bold text-yellow-600" /> }} /></p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold text-lg md:text-xl">4</div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base md:text-lg font-bold text-gray-800">{t('partner_dashboard.instructions.step4.title')}</h3>
                                <p className="text-sm md:text-base text-gray-600 mt-1 text-sm">{t('partner_dashboard.instructions.step4.text')}</p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 bg-blue-50 p-4 rounded text-center">
                        <p className="text-blue-800 font-bold mb-2">{t('partner_dashboard.instructions.payment_ready')}</p>
                        {!isStripeConnected ? <Link to="/partner-signup" className="inline-block bg-blue-600 text-white px-6 py-2 rounded shadow hover:bg-blue-700">{t('partner_dashboard.instructions.connect_stripe')}</Link> : <span className="text-green-600 font-bold">{t('partner_dashboard.instructions.stripe_connected')}</span>}
                    </div>
                </div>
            )}

            {/* TAB: MARKETING ASSETS (✅ KEPT UNTOUCHED, JUST WRAPPED IN GRID) */}
            {activeTab === 'Marketing Assets' && (
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded shadow">
                        <h3 className="font-bold mb-4 flex items-center gap-2"><span>🎨</span> {t('partner_dashboard.tabs.assets')}</h3>
                        <p className="text-xs text-gray-500 mb-4">Download ready-to-post images for your language ({currentLang.toUpperCase()}).</p>
                        
                        <div className="space-y-3">
                            <a href={`${assetsPath}/instagram.png`} download className="flex items-center w-full text-left p-3 border rounded hover:bg-gray-50 transition">
                                <span className="text-xl mr-3">📱</span>
                                <div><span className="block font-bold text-sm">Instagram Story</span><span className="text-xs text-gray-400">1080x1920 PNG</span></div>
                                <span className="ml-auto text-indigo-600 font-bold text-sm">⬇ Download</span>
                            </a>
                            {/* ✅ Case Sensitive Fix */}
                            <a href={`${assetsPath}/linkedIn.png`} download className="flex items-center w-full text-left p-3 border rounded hover:bg-gray-50 transition">
                                <span className="text-xl mr-3">💼</span>
                                <div><span className="block font-bold text-sm">LinkedIn Post</span><span className="text-xs text-gray-400">Square PNG</span></div>
                                <span className="ml-auto text-indigo-600 font-bold text-sm">⬇ Download</span>
                            </a>
                            <a href={`${assetsPath}/twitter.zip`} download className="flex items-center w-full text-left p-3 border rounded hover:bg-gray-50 transition">
                                <span className="text-xl mr-3">🐦</span>
                                <div><span className="block font-bold text-sm">X / Twitter Thread</span><span className="text-xs text-gray-400">ZIP (3 Images)</span></div>
                                <span className="ml-auto text-indigo-600 font-bold text-sm">⬇ Download</span>
                            </a>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded shadow">
                        <h3 className="font-bold mb-4 flex items-center gap-2"><span>✉️</span> Email Scripts</h3>
                        <div className="space-y-3">
                            <div className="p-3 border rounded bg-gray-50">
                                <h4 className="font-bold text-sm mb-1">Invitation to Colleagues</h4>
                                <button onClick={() => copyEmailScript('colleague')} className="w-full bg-white border border-indigo-200 text-indigo-600 py-1 rounded text-sm hover:bg-indigo-50">📋 Copy Subject & Body</button>
                            </div>
                            <div className="p-3 border rounded bg-gray-50">
                                <h4 className="font-bold text-sm mb-1">"Why Aulice?" Value Prop</h4>
                                <button onClick={() => copyEmailScript('value')} className="w-full bg-white border border-indigo-200 text-indigo-600 py-1 rounded text-sm hover:bg-indigo-50">📋 Copy Subject & Body</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: TEAM PERFORMANCE */}
            {activeTab === 'Team Performance' && (
                <div className="bg-white p-8 rounded shadow text-center">
                    {!isSenior ? <div className="py-10"><span className="text-5xl">🔒</span><h3 className="text-xl font-bold mt-4">Locked Feature</h3><p className="text-gray-600">Become a Senior Partner to unlock Team Commissions and view this tab.</p></div> : <div><h3 className="font-bold mb-4">Your Team (3% Override Active)</h3>{teamList.length > 0 ? <ul className="text-left">{teamList.map(m => <li key={m.id} className="border-b py-2">{m.displayName}</li>)}</ul> : <p>No team members yet.</p>}</div>}
                </div>
            )}

             {activeTab === 'Settings' && (
                <div className="bg-white p-6 rounded shadow">
                    <h3 className="font-bold mb-4">Account Settings</h3>
                    <p className="text-sm text-gray-600 mb-2">Email: {user.email}</p>
                    <div className="mt-4"><p className="font-bold text-sm">Stripe Connection:</p>{isStripeConnected ? <span className="text-green-600 text-sm">✅ Connected</span> : <Link to="/partner-signup" className="text-blue-600 text-sm underline">Connect Stripe Now</Link>}</div>
                </div>
            )}
        </div>
    );
};

export default PartnerDashboard;